'use client';

import { Builder } from '@builder.io/sdk';
import { motion, useReducedMotion } from 'framer-motion';

import type { ReactNode } from 'react';

type RevealProps = {
  children: ReactNode;
  className?: string;
  /** Stagger offset in seconds. */
  delay?: number;
  /** How far below its resting spot it starts, in px. */
  y?: number;
  /**
   * Above-the-fold mode: animate immediately on mount instead of on scroll, and
   * ONLY with a transform (opacity stays 1). The element is therefore never
   * blank on first paint, so there is no "reload" flash and the LCP is not
   * delayed. Use this for the masthead/hero; leave it off for everything below
   * the fold (which fades + rises on scroll).
   */
  mount?: boolean;
};

/**
 * Professional scroll-reveal (fade + rise, once) built on framer-motion but made
 * safe for this Builder/Next stack so it never causes the "double render/reload":
 *
 *  - Meant for BELOW-the-fold content only. Its initial hidden state is rendered
 *    on the server, but since the element isn't visible on load there is no flash
 *    on first paint. Keep the masthead (LCP, above the fold) STATIC — do not wrap
 *    it — so the hero never blanks then re-appears.
 *  - Inside the Builder editor components remount on every edit, which would
 *    replay the animation (the "double render" the team saw). There we render the
 *    children statically. `Builder.isEditing` is always false in production, so
 *    server and client agree and there is no hydration mismatch.
 *  - Honors prefers-reduced-motion (no transform/opacity animation).
 */
const Reveal = ({ children, className, delay = 0, y = 24, mount = false }: RevealProps) => {
  const reduce = useReducedMotion();

  if (Builder.isEditing) {
    return <div className={className}>{children}</div>;
  }

  if (mount) {
    return (
      <motion.div
        className={className}
        initial={reduce ? false : { y }}
        animate={reduce ? undefined : { y: 0 }}
        transition={{ duration: 0.6, delay, ease: [0.16, 1, 0.3, 1] }}
      >
        {children}
      </motion.div>
    );
  }

  return (
    <motion.div
      className={className}
      initial={reduce ? false : { opacity: 0, y }}
      whileInView={reduce ? undefined : { opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.15 }}
      transition={{ duration: 0.55, delay, ease: [0.16, 1, 0.3, 1] }}
    >
      {children}
    </motion.div>
  );
};

export default Reveal;
